import FontSize from 'client/components/core/Sidebar/components/Settings/components/Font/FontSize'
import TextFontUpdater from 'client/components/core/Sidebar/components/Settings/components/Font/TextFontUpdater'
import Input from 'client/components/core/Sidebar/components/Settings/components/Input/Input'
import {
  useUpdateEntity,
  useUpdateProp,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import { DelayOption } from 'client/components/core/Sidebar/components/Settings/options'
import DeviceAppearanceNew from 'client/components/core/Sidebar/components/Settings/options/DeviceAppearance/DeviceAppearanceNew'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import {
  ShadowStyle,
  TextAlignStyle,
} from 'client/components/core/Sidebar/components/Settings/styles'
import Border from 'client/components/core/Sidebar/components/Settings/styles/Border'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { ContentBoxInterface } from 'common/types/entities/ContentBoxInterface'
import React from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { Switcher } from 'src/editor/shared/ui-lib/switcher'

interface ContentBoxSettingsProps {
  entity: ContentBoxInterface
}

type HeaderProp = ContentBoxInterface['header']

const ContentBoxSettings = ({ entity }: ContentBoxSettingsProps) => {
  const updateProp = useUpdateProp(entity)
  const updateEntity = useUpdateEntity<ContentBoxInterface>()
  const updateHeaderEntity = (
    updatedHeaderEntity: ContentBoxInterface['header'],
  ) => updateEntity({ ...entity, header: updatedHeaderEntity })
  const updateHeaderProp =
    (key: keyof HeaderProp) => (value: HeaderProp[keyof HeaderProp]) =>
      updateProp('header')({ ...entity.header, [key]: value })

  const { isMobile } = useDeviceModeContext()

  return (
    <>
      <GroupTitle isFirst>header options</GroupTitle>
      <Switcher
        btnList={[
          { value: true, content: 'On' },
          { value: false, content: 'Off' },
        ]}
        value={entity.header.visibility}
        labelText={'header visibility'}
        update={updateHeaderProp('visibility')}
      />
      {entity.header.visibility && (
        <Input
          type={'text'}
          update={updateHeaderProp('title')}
          value={entity.header.title || ''}
          label={'header title'}
        />
      )}
      <FontSize
        label={'settings_styles.font_size.label'}
        fontSize={entity.header.fontSize}
        mobileFontSize={entity.header.mobileFontSize}
        onChange={value => updateHeaderProp('fontSize')(value)}
        onChangeMobile={value => updateHeaderProp('mobileFontSize')(value)}
      />
      <TextFontUpdater<ContentBoxInterface['header']>
        entity={entity.header}
        update={updateHeaderEntity}
        fontFamilyKey={'fontFamily'}
        fontWeightKey={'fontWeight'}
        fontStyleKey={'fontStyle'}
        mobileFontFamilyKey={'mobileFontFamily'}
        mobileFontWeightKey={'mobileFontWeight'}
        mobileFontStyleKey={'mobileFontStyle'}
        fontFileIdKey={'fontFileId'}
        mobileFontFileIdKey={'mobileFontFileId'}
      />
      <ColorPickerControl
        label={'settings_styles.background_color.label'}
        color={entity.header.backgroundColor}
        mobileColor={entity.header.mobileBackgroundColor}
        update={bgColor => updateHeaderProp('backgroundColor')(bgColor)}
        mobileUpdate={mobileBgColor =>
          updateHeaderProp('mobileBackgroundColor')(mobileBgColor)
        }
      />
      <ColorPickerControl
        color={entity.header.color}
        mobileColor={entity.header.mobileColor}
        update={color => updateHeaderProp('color')(color)}
        mobileUpdate={mobileColor =>
          updateHeaderProp('mobileColor')(mobileColor)
        }
      />
      <TextAlignStyle
        mobileTextAlign={entity.header.mobileTextAlign || 'left'}
        textAlign={entity.header.textAlign || 'left'}
        update={updateHeaderProp('textAlign')}
        mobileUpdate={updateHeaderProp('mobileTextAlign')}
      />
      <GroupTitle>color</GroupTitle>
      <ColorPickerControl
        label={'settings_styles.background_color.label'}
        color={entity.backgroundColor}
        mobileColor={entity.mobileBackgroundColor}
        update={bgColor => updateProp('backgroundColor')(bgColor)}
        mobileUpdate={mobileBgColor =>
          updateProp('mobileBackgroundColor')(mobileBgColor)
        }
      />
      <ShadowStyle
        shadow={entity.boxShadow}
        mobileShadow={entity.mobileBoxShadow}
        update={updateProp('boxShadow')}
        mobileUpdate={updateProp('mobileBoxShadow')}
      />
      <Border
        border={entity.border}
        mobileBorder={entity.mobileBorder}
        update={updateProp('border')}
        mobileUpdate={updateProp('mobileBorder')}
      />
      <DelayOption update={updateProp('delay')} delay={entity.delay} />
      <SizeAndPositionGroup
        padding={isMobile ? entity?.mobilePadding : entity.padding}
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      <DeviceAppearanceNew entity={entity} />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </>
  )
}

export default ContentBoxSettings
